import { useState } from 'react';
import { useResourceKit } from '@resource-kit/core/react';

/** The two forms of one list (#2680): the table and the cards. */
export const LIST_LAYOUT = { table: 'table', cards: 'cards' } as const;

export type ListLayout = (typeof LIST_LAYOUT)[keyof typeof LIST_LAYOUT];

const LAYOUTS: readonly unknown[] = Object.values(LIST_LAYOUT);

function isListLayout(value: unknown): value is ListLayout {
  return LAYOUTS.includes(value);
}

export interface ListLayoutHandle {
  layout: ListLayout;
  setLayout: (layout: ListLayout) => void;
}

/**
 * The persisted table/cards choice of one list (#2680).
 *
 * Stored through the kit's `storage` seam under `listLayout.<base>`, beside
 * the column visibility's `columns.<base>`, so it is per viewer and per list
 * on this machine. A stored value that is not a layout is deleted and the list
 * falls back to the table — the same read-then-`has` discard as
 * `useColumnVisibility`'s `readVisibility`: an ABSENT key is not corrupt and
 * must not turn a first mount into a write.
 */
export function useListLayout(base: string): ListLayoutHandle {
  const { storage } = useResourceKit();
  const storageKey = `listLayout.${base}`;

  const [layout, setLayoutState] = useState<ListLayout>(() => {
    const raw = storage.read(storageKey);
    if (isListLayout(raw)) return raw;
    if (storage.has(storageKey)) storage.remove(storageKey);
    return LIST_LAYOUT.table;
  });

  const setLayout = (next: ListLayout) => {
    setLayoutState(next);
    storage.write(storageKey, next);
  };

  return { layout, setLayout };
}
